import { DollarSign, Users, TrendingUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer,
} from 'recharts';
import { useObsOverview, useObsCostBySource } from '@/hooks/useObservability';
import { StatCard, LoaderBlock, fmtUSD, fmtNum, fmtMonth } from './shared';

export function ObsOverviewTab() {
  const { t } = useTranslation();
  const { data: overview = [], isLoading } = useObsOverview();
  const { data: bySource = [] } = useObsCostBySource();

  if (isLoading) return <LoaderBlock />;

  const latest = overview[overview.length - 1];
  const prev = overview[overview.length - 2];
  const ytdAi = overview.reduce((s, r) => s + (r.ai_subs_usd || 0), 0);
  const ytdTotal = overview.reduce((s, r) => s + (r.total_subs_usd || 0), 0);

  // Pivot bySource for stacked chart
  const sourceMonths = [...new Set(bySource.map((r) => r.month.substring(0, 7)))].sort();
  const sourcePivot = sourceMonths.map((m) => {
    const row: any = { month: m };
    bySource.filter((r) => r.month.startsWith(m)).forEach((r) => { row[r.source_label] = r.usd; });
    return row;
  });

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={DollarSign} tone="purple" label={t('observability.overview.ytd_ai_cost')} value={fmtUSD(ytdAi)}
                  sub={t('observability.overview.pct_of_total', { pct: ((ytdAi / ytdTotal) * 100).toFixed(1) })} />
        <StatCard icon={DollarSign} tone="blue" label={t('observability.overview.last_month_ai')} value={fmtUSD(latest?.ai_subs_usd)}
                  sub={prev ? `vs ${fmtUSD(prev.ai_subs_usd)} prev` : undefined} />
        <StatCard icon={Users} tone="green" label={t('observability.overview.active_ai_users')} value={fmtNum(latest?.ai_active_users)}
                  sub={`Gemini ${latest?.gemini_users} · CC ${latest?.claude_code_users} · MCP ${latest?.mcp_users}`} />
        <StatCard icon={TrendingUp} tone="amber" label={t('observability.overview.cost_per_user')} value={fmtUSD(latest?.cost_per_active_user)}
                  sub={t('observability.overview.current_month')} />
      </div>

      <Card className="p-5">
        <h3 className="text-sm font-semibold mb-3">{t('observability.overview.monthly_evolution')}</h3>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={overview} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" tickFormatter={fmtMonth} />
            <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
            <Tooltip formatter={(v: any) => fmtUSD(v)} labelFormatter={(m) => fmtMonth(String(m))} />
            <Bar dataKey="ai_subs_usd" fill="#8b5cf6" name="AI subs (EBITDA)" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <Card className="p-5">
        <h3 className="text-sm font-semibold mb-1">{t('observability.overview.cost_by_source')}</h3>
        <p className="text-xs text-muted-foreground mb-3">
          {t('observability.overview.cost_by_source_note')}
        </p>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={sourcePivot} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" tickFormatter={fmtMonth} />
            <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
            <Tooltip formatter={(v: any) => fmtUSD(v)} labelFormatter={(m) => fmtMonth(String(m))} />
            <Legend />
            <Line type="monotone" dataKey="Subscriptions (Ramp)" stroke="#8b5cf6" strokeWidth={2} />
            <Line type="monotone" dataKey="Anthropic Cost API (analytic)" stroke="#3b82f6" strokeWidth={2} strokeDasharray="4 2" />
            <Line type="monotone" dataKey="Claude Code estimated (analytic)" stroke="#10b981" strokeWidth={2} strokeDasharray="4 2" />
            <Line type="monotone" dataKey="MCP/Agentic estimated (already in Ramp)" stroke="#f59e0b" strokeWidth={2} strokeDasharray="4 2" />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      <Card className="p-5">
        <h3 className="text-sm font-semibold mb-3">{t('observability.overview.adoption_by_source')}</h3>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={overview} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" tickFormatter={fmtMonth} />
            <YAxis />
            <Tooltip labelFormatter={(m) => fmtMonth(String(m))} />
            <Legend />
            <Bar dataKey="gemini_users" stackId="a" fill="#10b981" name="Gemini users" />
            <Bar dataKey="claude_code_users" stackId="a" fill="#8b5cf6" name="Claude Code users" />
            <Bar dataKey="mcp_users" stackId="a" fill="#f59e0b" name="MCP users" />
          </BarChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );
}
